/**
 * ColorCodedMetricsTable - Professional sports analytics data table
 *
 * Features:
 * - Color-coded score cells (red/orange/yellow/green/blue spectrum)
 * - Category groupings with sub-categories
 * - Responsive design
 * - Sortable columns
 * - Hover highlights
 *
 * Inspired by professional athlete assessment reports
 */

import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Info } from 'lucide-react';

// Score color mappings - matches professional sports analytics
const SCORE_COLORS = {
  poor: { bg: '#dc2626', text: '#ffffff', label: 'Poor' },         // Red (0-20)
  belowAvg: { bg: '#f97316', text: '#ffffff', label: 'Below Avg' }, // Orange (21-40)
  average: { bg: '#eab308', text: '#000000', label: 'Average' },    // Yellow (41-60)
  aboveAvg: { bg: '#22c55e', text: '#ffffff', label: 'Above Avg' }, // Green (61-80)
  excellent: { bg: '#3b82f6', text: '#ffffff', label: 'Excellent' }, // Blue (81-100)
};

const INVERTED_SCORE_COLORS = {
  excellent: { bg: '#dc2626', text: '#ffffff', label: 'High Risk' },   // Red is bad
  aboveAvg: { bg: '#f97316', text: '#ffffff', label: 'Medium Risk' },  // Orange
  average: { bg: '#eab308', text: '#000000', label: 'Low Risk' },      // Yellow
  belowAvg: { bg: '#22c55e', text: '#ffffff', label: 'Minimal' },      // Green
  poor: { bg: '#3b82f6', text: '#ffffff', label: 'Clean' },            // Blue is good
};

function getScoreColor(value, inverted = false) {
  const colors = inverted ? INVERTED_SCORE_COLORS : SCORE_COLORS;

  if (value >= 81) return inverted ? colors.excellent : colors.excellent;
  if (value >= 61) return inverted ? colors.aboveAvg : colors.aboveAvg;
  if (value >= 41) return colors.average;
  if (value >= 21) return inverted ? colors.belowAvg : colors.belowAvg;
  return inverted ? colors.poor : colors.poor;
}

/**
 * ScoreCell - Individual score cell with color coding
 */
function ScoreCell({ value, inverted = false, showLabel = false }) {
  const colors = getScoreColor(value, inverted);

  return (
    <div
      className="inline-flex items-center justify-center min-w-[50px] px-3 py-1.5 rounded font-mono font-bold text-sm"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {Math.round(value)}
      {showLabel && (
        <span className="ml-2 text-xs font-normal opacity-80">{colors.label}</span>
      )}
    </div>
  );
}

/**
 * MetricsTable - Full-featured metrics table
 */
export default function ColorCodedMetricsTable({
  title,
  data,
  columns = ['category', 'subCategory', 'score', 'description'],
  invertedScores = false,
  showLegend = true,
  className = '',
}) {
  const [sortColumn, setSortColumn] = useState('score');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Group data by category
  const groupedData = data.reduce((acc, row) => {
    const category = row.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(row);
    return acc;
  }, {});

  // Sort rows within each category
  const sortedGroupedData = Object.entries(groupedData).map(([category, rows]) => {
    const sortedRows = [...rows].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    return [category, sortedRows];
  });

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  return (
    <div className={`bg-zinc-800/30 border border-zinc-700 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      {title && (
        <div className="px-4 py-3 border-b border-zinc-700 bg-zinc-800/50">
          <h3 className="text-lg font-bold text-white">{title}</h3>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-900/50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                Sub-category
              </th>
              <th
                className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('score')}
              >
                <div className="flex items-center justify-center gap-1">
                  Score
                  {sortColumn === 'score' && (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {sortedGroupedData.map(([category, rows]) => (
              rows.map((row, idx) => (
                <tr
                  key={`${category}-${idx}`}
                  className="hover:bg-zinc-800/50 transition-colors group"
                >
                  {idx === 0 ? (
                    <td
                      className="px-4 py-3 font-semibold text-white"
                      rowSpan={rows.length}
                    >
                      {category}
                    </td>
                  ) : null}
                  <td className="px-4 py-3 text-zinc-300 text-sm italic">
                    {row.subCategory}
                    {row.inverted && (
                      <span className="ml-1 text-zinc-500">*</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ScoreCell
                      value={row.score}
                      inverted={row.inverted ?? invertedScores}
                    />
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm max-w-[300px]">
                    {row.description}
                  </td>
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="px-4 py-3 border-t border-zinc-700 bg-zinc-900/30">
          <div className="flex flex-wrap items-center gap-3">
            {Object.entries(invertedScores ? INVERTED_SCORE_COLORS : SCORE_COLORS).map(([key, colors]) => (
              <div key={key} className="flex items-center gap-1.5">
                <div
                  className="w-4 h-4 rounded-sm"
                  style={{ backgroundColor: colors.bg }}
                />
                <span className="text-xs text-zinc-500 uppercase">{colors.label}</span>
              </div>
            ))}
          </div>
          {data.some(row => row.inverted) && (
            <p className="mt-2 text-xs text-zinc-600">* Higher scores considered worse</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * CompactMetricsRow - Single row metrics display for inline use
 */
export function CompactMetricsRow({ items, inverted = false }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-zinc-800/50 rounded">
          <span className="text-xs text-zinc-400">{item.label}</span>
          <ScoreCell value={item.value} inverted={item.inverted ?? inverted} />
        </div>
      ))}
    </div>
  );
}

/**
 * TippingMetricsTable - Specialized table for pitch tipping data
 */
export function TippingMetricsTable({ pitcherData, className = '' }) {
  // Transform pitcher data into table format
  const metrics = [];

  // Add pitch type breakdown
  if (pitcherData.byPitchType) {
    Object.entries(pitcherData.byPitchType).forEach(([pitchType, stats]) => {
      metrics.push({
        category: 'Pitch Types',
        subCategory: pitchType,
        score: (stats.tippingRate || 0) * 100,
        description: `${stats.totalCount || stats.count} pitches, ${stats.tippingDetectedCount || stats.tippingCount} with tells`,
        inverted: true, // Higher tipping rate is worse
      });
    });
  }

  // Add critical tells as metrics
  if (pitcherData.criticalTells) {
    pitcherData.criticalTells.forEach((tell) => {
      metrics.push({
        category: 'Critical Tells',
        subCategory: tell.tell.substring(0, 30) + (tell.tell.length > 30 ? '...' : ''),
        score: (tell.confidence || 0) * 100,
        description: tell.pitchTypesAffected?.join(', ') || 'All pitches',
        inverted: true,
      });
    });
  }

  return (
    <ColorCodedMetricsTable
      title="Tipping Analysis Breakdown"
      data={metrics}
      invertedScores={true}
      className={className}
    />
  );
}

/**
 * PitchTypeScoreCard - Card showing pitch type with score
 */
export function PitchTypeScoreCard({
  pitchType,
  score,
  pitches,
  tippingCount,
  commonTells = [],
  inverted = true,
}) {
  const colors = getScoreColor(score, inverted);

  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 hover:border-zinc-600 transition-all group">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-lg font-bold text-white">{pitchType}</h4>
          <p className="text-xs text-zinc-500">{pitches} pitches analyzed</p>
        </div>
        <div
          className="px-3 py-1.5 rounded-lg font-mono font-bold text-lg"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          {Math.round(score)}%
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden mb-3">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${score}%`, backgroundColor: colors.bg }}
        />
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-zinc-400">Tipping Detected</span>
        <span className="text-white font-mono">{tippingCount}</span>
      </div>

      {/* Common tells */}
      {commonTells.length > 0 && (
        <div className="pt-3 border-t border-zinc-700">
          <p className="text-xs text-zinc-500 mb-2">Common Tells:</p>
          <div className="flex flex-wrap gap-1">
            {commonTells.slice(0, 2).map((tell, idx) => (
              <span
                key={idx}
                className="px-2 py-0.5 text-xs bg-zinc-700 text-zinc-300 rounded truncate max-w-full"
                title={tell}
              >
                {tell.length > 20 ? tell.substring(0, 20) + '...' : tell}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
